import { useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  CampaignPlanTier,
  PeopleUploadConfirmation,
  PeopleUploadPreview,
  PeopleUploadPreviewRow,
  PeopleUploadRow,
} from "@shared-core/data/api-types";
import { parseCampaignPeopleCsv } from "@shared-core/domain/campaign-people-csv";
import { ADDRESS_SOURCE_LABELS } from "@shared-core/domain/direct-mail-campaigns";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import { Checkbox } from "../../app/components/primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../app/components/primitives/dialog";
import { Input } from "../../app/components/primitives/input";
import { Label } from "../../app/components/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/primitives/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { failureLogFields, readCampaignActionError } from "./actionErrors";

/** The value of the possible-match picker that means "a new contact". */
const NEW_CONTACT = "__new__";

interface Upload {
  filename: string;
  /** Generated once per file chosen: a retried confirm adds nothing twice. */
  uploadId: string;
  rows: PeopleUploadRow[];
  preview: PeopleUploadPreview;
}

type Stage =
  | { kind: "choose"; problems: string[] }
  | { kind: "reading" }
  | { kind: "preview"; upload: Upload }
  | { kind: "adding"; upload: Upload };

/** A possible match's choice: a person id, "new contact" (null), or not
 *  chosen yet (absent). */
type Resolutions = Record<number, string | null>;

function initialResolutions(preview: PeopleUploadPreview): Resolutions {
  const resolutions: Resolutions = {};
  for (const row of preview.rows) {
    if (
      row.outcome === "possible_existing_contact" &&
      row.preselected_person_id
    ) {
      resolutions[row.row_index] = row.preselected_person_id;
    }
  }
  return resolutions;
}

/** The server names a refused choice by its place in the resolutions list
 * (`resolutions.<position>`); the preview shows it beside its row
 * (`resolutions.<row_index>`). */
function byUploadRow(
  fields: Record<string, string>,
  resolutionList: { row_index: number }[],
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(fields).map(([field, message]) => {
      const match = /^resolutions\.(\d+)$/.exec(field);
      const resolution = match ? resolutionList[Number(match[1])] : undefined;
      return [
        resolution ? `resolutions.${resolution.row_index}` : field,
        message,
      ];
    }),
  );
}

function peopleCount(n: number): string {
  return `${n} ${n === 1 ? "person" : "people"}`;
}

function rowName(row: PeopleUploadRow | undefined): string {
  if (!row) return "";
  const name = [row.first_name, row.last_name].filter(Boolean).join(" ");
  return name || row.email || "";
}

/** "Job title", "Postal address" from a backend field name. */
function fieldLabel(field: string): string {
  const words = field.replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * "Add people and tiers": upload a CSV with a tier column, see what it
 * would do, then confirm. There is one list, the campaign's audience:
 * people already in it just get their tier, and anyone not yet in it is
 * also added to it (never to a separate campaign copy). The preview shows
 * new vs already in CIS, possible matches to choose between, what will
 * change on contacts already in CIS, the tier per row, address gaps, rows
 * that can't be used and why, and — when the audience is shared with other
 * campaigns — asks the user to acknowledge that before confirming. The
 * server re-runs every check on confirm.
 */
export function AddPeopleDialog({
  campaignId,
  audienceName,
  tiers,
  open,
  onOpenChange,
  onAdded,
}: {
  campaignId: string;
  /** The campaign's audience: the one list people are added to. */
  audienceName: string;
  tiers: CampaignPlanTier[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdded: (result: PeopleUploadConfirmation) => void;
}) {
  const dataAdapter = useDataAdapter();
  const [stage, setStage] = useState<Stage>({ kind: "choose", problems: [] });
  const [resolutions, setResolutions] = useState<Resolutions>({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  function reset(nextOpen: boolean) {
    if (!nextOpen) {
      setStage({ kind: "choose", problems: [] });
      setResolutions({});
      setAcknowledged(false);
      setFieldErrors({});
      setError(null);
    }
    onOpenChange(nextOpen);
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setError(null);
    setFieldErrors({});
    setStage({ kind: "reading" });
    const parsed = parseCampaignPeopleCsv(await file.text());
    if (parsed.errors.length > 0) {
      setStage({ kind: "choose", problems: parsed.errors });
      return;
    }
    const uploadId = crypto.randomUUID();
    try {
      const preview = await dataAdapter.previewCampaignPeopleUpload(
        campaignId,
        { uploadId, rows: parsed.rows },
      );
      setResolutions(initialResolutions(preview));
      setAcknowledged(false);
      setStage({
        kind: "preview",
        upload: { filename: file.name, uploadId, rows: parsed.rows, preview },
      });
    } catch (err) {
      console.error(
        "marketing.campaign.upload_preview.failed",
        failureLogFields(err, { campaign_id: campaignId, upload_id: uploadId }),
      );
      const read = readCampaignActionError(
        err,
        "Couldn't read that file. Check it's a CSV and try again.",
      );
      setStage({ kind: "choose", problems: [read.summary] });
    }
  }

  async function handleConfirm(upload: Upload) {
    const unanswered = upload.preview.rows.filter(
      (r) =>
        r.outcome === "possible_existing_contact" &&
        !(r.row_index in resolutions),
    );
    if (unanswered.length > 0) {
      setFieldErrors(
        Object.fromEntries(
          unanswered.map((r) => [
            `resolutions.${r.row_index}`,
            "Choose who this is, or a new contact",
          ]),
        ),
      );
      setError(
        `Choose who ${unanswered.length === 1 ? "1 possible match is" : `${unanswered.length} possible matches are`} before adding.`,
      );
      return;
    }
    setStage({ kind: "adding", upload });
    setError(null);
    setFieldErrors({});
    const resolutionList = Object.entries(resolutions).map(
      ([rowIndex, personId]) => ({
        row_index: Number(rowIndex),
        person_id: personId,
      }),
    );
    try {
      const result = await dataAdapter.confirmCampaignPeopleUpload(campaignId, {
        uploadId: upload.uploadId,
        rows: upload.rows,
        resolutions: resolutionList,
        acknowledgeSharedAudience: acknowledged,
      });
      onAdded(result);
      reset(false);
    } catch (err) {
      console.error(
        "marketing.campaign.upload_confirm.failed",
        failureLogFields(err, {
          campaign_id: campaignId,
          upload_id: upload.uploadId,
        }),
      );
      const read = readCampaignActionError(
        err,
        "Couldn't add these people. Try again: anyone already added won't be added twice.",
      );
      setFieldErrors(byUploadRow(read.fields, resolutionList));
      setError(read.summary);
      setStage({ kind: "preview", upload });
    }
  }

  const upload =
    stage.kind === "preview" || stage.kind === "adding" ? stage.upload : null;

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add people and tiers</DialogTitle>
          <DialogDescription>
            Upload a CSV with a <strong>tier</strong> column
            {tiers.length > 0 &&
              ` (${tiers.map((t) => t.name).join(", ")})`}{" "}
            plus name, company, job title, email, phone and address. Everyone in
            it gets their tier in this campaign. Anyone not yet in the{" "}
            {audienceName} audience is added to it, so there is still one list.
            People not yet in One will be added as prospects. For people already
            in One, blank cells won&apos;t overwrite what One holds, Tharstern
            details will be left alone and the tags will be kept.
          </DialogDescription>
        </DialogHeader>

        {(stage.kind === "choose" || stage.kind === "reading") && (
          <div className="space-y-2 py-2">
            <Label htmlFor="campaign-upload-file">List to upload (CSV)</Label>
            <Input
              id="campaign-upload-file"
              type="file"
              accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values"
              onChange={(e) => void handleFileChange(e)}
              disabled={stage.kind === "reading"}
              aria-invalid={
                stage.kind === "choose" && stage.problems.length > 0
                  ? true
                  : undefined
              }
              aria-describedby={
                stage.kind === "choose" && stage.problems.length > 0
                  ? "campaign-upload-file-problems"
                  : undefined
              }
            />
            {stage.kind === "reading" && (
              <p className="text-sm text-muted-foreground">Reading the file…</p>
            )}
            {stage.kind === "choose" && stage.problems.length > 0 && (
              <ul
                id="campaign-upload-file-problems"
                role="alert"
                className="text-sm text-red-700 dark:text-red-300 list-disc pl-5"
              >
                {stage.problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {upload && (
          <UploadPreview
            upload={upload}
            audienceName={audienceName}
            resolutions={resolutions}
            onResolve={(rowIndex, personId) => {
              setResolutions((prev) => ({ ...prev, [rowIndex]: personId }));
              setFieldErrors((prev) => {
                const next = { ...prev };
                delete next[`resolutions.${rowIndex}`];
                return next;
              });
            }}
            acknowledged={acknowledged}
            onAcknowledge={setAcknowledged}
            fieldErrors={fieldErrors}
            disabled={stage.kind === "adding"}
          />
        )}

        {error && (
          <p className="text-sm text-red-700 dark:text-red-300" role="alert">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => reset(false)}>
            Cancel
          </Button>
          {upload && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setError(null);
                  setFieldErrors({});
                  setStage({ kind: "choose", problems: [] });
                }}
                disabled={stage.kind === "adding"}
              >
                Choose another file
              </Button>
              <ConfirmButton
                upload={upload}
                adding={stage.kind === "adding"}
                acknowledged={acknowledged}
                onConfirm={() => void handleConfirm(upload)}
              />
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function usableRows(preview: PeopleUploadPreview): PeopleUploadPreviewRow[] {
  return preview.rows.filter((r) => r.outcome !== "skipped");
}

function ConfirmButton({
  upload,
  adding,
  acknowledged,
  onConfirm,
}: {
  upload: Upload;
  adding: boolean;
  acknowledged: boolean;
  onConfirm: () => void;
}) {
  const count = usableRows(upload.preview).length;
  const needsAcknowledgement =
    upload.preview.needs_shared_audience_acknowledgement && !acknowledged;
  return (
    <Button
      onClick={onConfirm}
      disabled={adding || count === 0 || needsAcknowledgement}
    >
      {adding
        ? "Adding…"
        : `Set ${count === 1 ? "tier" : "tiers"} for ${peopleCount(count)}`}
    </Button>
  );
}

function UploadPreview({
  upload,
  audienceName,
  resolutions,
  onResolve,
  acknowledged,
  onAcknowledge,
  fieldErrors,
  disabled,
}: {
  upload: Upload;
  audienceName: string;
  resolutions: Resolutions;
  onResolve: (rowIndex: number, personId: string | null) => void;
  acknowledged: boolean;
  onAcknowledge: (value: boolean) => void;
  fieldErrors: Record<string, string>;
  disabled: boolean;
}) {
  const { preview } = upload;
  const usable = usableRows(preview);
  const skipped = preview.rows.filter((r) => r.outcome === "skipped");
  const inAudienceCount = usable.filter((r) => r.in_audience).length;
  const newToCisCount = usable.filter((r) => r.outcome === "new").length;
  const noAddressCount = usable.filter(
    (r) => r.address_source === "none",
  ).length;
  const possibleCount = usable.filter(
    (r) => r.outcome === "possible_existing_contact",
  ).length;
  const changing = usable.filter((r) => r.changes.length > 0);

  return (
    <div className="space-y-4 py-2">
      <p className="text-sm text-foreground">
        <strong>{upload.filename}</strong>: {peopleCount(usable.length)} will
        get a tier
        {skipped.length > 0 &&
          `, ${skipped.length} ${skipped.length === 1 ? "row" : "rows"} skipped`}
        .
      </p>
      <ul className="list-disc pl-5 text-sm text-foreground space-y-0.5">
        {inAudienceCount > 0 && (
          <li>
            {peopleCount(inAudienceCount)}{" "}
            {inAudienceCount === 1 ? "is" : "are"} already in the {audienceName}{" "}
            audience and just{" "}
            {inAudienceCount === 1 ? "gets their tier" : "get their tier"}.
          </li>
        )}
        {preview.new_to_audience_count > 0 && (
          <li>
            <strong>
              {peopleCount(preview.new_to_audience_count)} will also be added to
              the {audienceName} audience
            </strong>
            {newToCisCount > 0 && ` (${newToCisCount} of them new to One)`}.
          </li>
        )}
        {possibleCount > 0 && (
          <li>
            {possibleCount === 1
              ? "1 row has no email and may already be in One. Choose the person below."
              : `${possibleCount} rows have no email and may already be in One. Choose the person for each row below.`}
          </li>
        )}
      </ul>

      {preview.new_tier_names.length > 0 && (
        <p className="text-sm text-foreground">
          {newTiersLine(preview.new_tier_names)}
        </p>
      )}

      {preview.needs_shared_audience_acknowledgement && (
        <div className="rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-3 py-2 space-y-2">
          <p
            id="shared-audience-warning"
            className="text-sm text-amber-900 dark:text-amber-200"
          >
            This audience is also used by{" "}
            {preview.shared_audience_campaigns.map((c) => c.name).join(", ")}.
            The {preview.new_to_audience_count} new people will be in their
            future sends too.
          </p>
          <div className="flex items-center gap-2">
            <Checkbox
              id="acknowledge-shared-audience"
              checked={acknowledged}
              disabled={disabled}
              onCheckedChange={(checked) => onAcknowledge(checked === true)}
              aria-describedby="shared-audience-warning"
              aria-invalid={
                fieldErrors.acknowledge_shared_audience ? true : undefined
              }
            />
            <Label
              htmlFor="acknowledge-shared-audience"
              className="font-normal text-amber-900 dark:text-amber-200"
            >
              I understand – add them to the shared audience
            </Label>
          </div>
          {fieldErrors.acknowledge_shared_audience && (
            <p className="text-sm text-red-700 dark:text-red-300">
              {fieldErrors.acknowledge_shared_audience}
            </p>
          )}
        </div>
      )}

      {noAddressCount > 0 && (
        <p className="rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
          {noAddressCount === 1
            ? "1 person has no address"
            : `${noAddressCount} people have no address`}
          . They'll be added, but left out of mailings until an address is
          added.
        </p>
      )}

      {usable.length > 0 && (
        <div className="rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-14">Row</TableHead>
                <TableHead>Person</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead>Audience</TableHead>
                <TableHead>In One</TableHead>
                <TableHead>Address</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usable.map((row) => (
                <PreviewRow
                  key={row.row_index}
                  row={row}
                  uploaded={upload.rows[row.row_index]}
                  resolution={resolutions[row.row_index]}
                  onResolve={(personId) => onResolve(row.row_index, personId)}
                  error={fieldErrors[`resolutions.${row.row_index}`]}
                  disabled={disabled}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {changing.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-1">
            Will change on contacts already in One ({changing.length})
          </h3>
          <ul className="rounded-lg border border-border divide-y divide-border text-sm">
            {changing.map((row) => (
              <li key={row.row_index} className="px-3 py-2">
                <span className="font-medium">
                  {rowName(upload.rows[row.row_index])}
                </span>
                <ul className="mt-0.5 text-muted-foreground">
                  {row.changes.map((change) => (
                    <li key={change.field}>
                      {fieldLabel(change.field)}: {change.current ?? "blank"} →{" "}
                      <span className="text-foreground">
                        {change.new ?? "blank"}
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}

      {skipped.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-1">
            Rows that can't be used ({skipped.length})
          </h3>
          <p className="text-xs text-muted-foreground mb-2">
            Fix these in your sheet and upload it again. People already given a
            tier won't be duplicated.
          </p>
          <ul className="rounded-lg border border-border divide-y divide-border text-sm">
            {skipped.map((row) => (
              <li
                key={row.row_index}
                className="flex flex-wrap gap-x-3 px-3 py-2"
              >
                <span className="tabular-nums text-muted-foreground w-14">
                  Row {row.row_index + 1}
                </span>
                <span className="text-foreground">
                  {rowName(upload.rows[row.row_index]) || "(no name)"}
                </span>
                <span className="text-red-700 dark:text-red-300">
                  {row.skip_reason}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function newTiersLine(names: string[]): string {
  return `New ${names.length === 1 ? "tier" : "tiers"} this file sets up: ${names.join(", ")}.`;
}

function PreviewRow({
  row,
  uploaded,
  resolution,
  onResolve,
  error,
  disabled,
}: {
  row: PeopleUploadPreviewRow;
  uploaded: PeopleUploadRow | undefined;
  /** The chosen person (or null for a new contact); undefined if unchosen. */
  resolution: string | null | undefined;
  onResolve: (personId: string | null) => void;
  error: string | undefined;
  disabled: boolean;
}) {
  const errorId = `upload-row-${row.row_index}-error`;
  return (
    <TableRow>
      <TableCell className="align-top tabular-nums text-muted-foreground">
        {row.row_index + 1}
      </TableCell>
      <TableCell className="align-top whitespace-normal">
        <div className="font-medium">{rowName(uploaded)}</div>
        <div className="text-xs text-muted-foreground">
          {uploaded?.company_name}
        </div>
        {row.is_shared_mailbox && (
          <div className="text-xs text-amber-800 dark:text-amber-300">
            Several contacts share this email; the first one is used
          </div>
        )}
      </TableCell>
      <TableCell className="align-top whitespace-normal">
        <Badge variant="outline">{row.tier_name}</Badge>
        {row.is_new_tier && (
          <div className="text-xs text-muted-foreground">New tier</div>
        )}
        {row.current_tier_name && row.current_tier_name !== row.tier_name && (
          <div className="text-xs text-muted-foreground">
            Moves from {row.current_tier_name}
          </div>
        )}
      </TableCell>
      <TableCell className="align-top whitespace-normal">
        {row.in_audience ? (
          <span className="text-muted-foreground">
            Already in – gets their tier
          </span>
        ) : (
          <span className="font-medium text-foreground">Will be added</span>
        )}
      </TableCell>
      <TableCell className="align-top whitespace-normal min-w-56">
        {row.outcome === "new" && <Badge variant="success">New to One</Badge>}
        {row.outcome === "already_in_cis" && (
          <Badge variant="neutral">Already in One</Badge>
        )}
        {row.outcome === "possible_existing_contact" && (
          <div className="space-y-1">
            <Badge variant="warning">Possible match</Badge>
            <Select
              value={
                resolution === undefined
                  ? ""
                  : resolution === null
                    ? NEW_CONTACT
                    : resolution
              }
              onValueChange={(v) => onResolve(v === NEW_CONTACT ? null : v)}
              disabled={disabled}
            >
              <SelectTrigger
                aria-label={`Who row ${row.row_index + 1} is`}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                className="h-8"
              >
                <SelectValue placeholder="Choose who this is" />
              </SelectTrigger>
              <SelectContent>
                {row.candidates.map((c) => (
                  <SelectItem key={c.person_id} value={c.person_id}>
                    {[c.name, c.company_name, c.postcode]
                      .filter(Boolean)
                      .join(" · ")}
                    {c.in_this_campaign_tier && " (already in this campaign)"}
                  </SelectItem>
                ))}
                <SelectItem value={NEW_CONTACT}>
                  None of these – a new contact
                </SelectItem>
              </SelectContent>
            </Select>
            {error && (
              <p
                id={errorId}
                className="text-xs text-red-700 dark:text-red-300"
              >
                {error}
              </p>
            )}
          </div>
        )}
      </TableCell>
      <TableCell
        className={
          row.address_source === "none"
            ? "align-top font-medium text-red-700 dark:text-red-300"
            : "align-top"
        }
      >
        {row.address_source ? ADDRESS_SOURCE_LABELS[row.address_source] : "–"}
      </TableCell>
    </TableRow>
  );
}
